import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check for saved theme preference first
    if (typeof window === 'undefined') return false;
    
    const savedTheme = localStorage.getItem('maxicar-theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    
    // Fall back to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply theme to document
  const applyTheme = (darkMode) => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
    }
  };

  // Toggle theme
  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Apply theme when isDarkMode changes
  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('maxicar-theme', isDarkMode ? 'dark' : 'light');
    
    // Apply to document
    applyTheme(isDarkMode);
    
    // Log for debugging
    console.log('Theme set to:', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Initial theme application
  useEffect(() => {
    applyTheme(isDarkMode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    isDarkMode,
    toggleTheme,
    theme: isDarkMode ? 'dark' : 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
